import { useMemo } from "react";
import DataSelectionModel from "../DataSelectionModal";
import { useDataSelectionContext } from "../DataSelectionContext";
import { Col, Form, InputGroup, Row } from "react-bootstrap";
import TimeSelectGroup from "../../../@ui/TimeSelectGroup";
import { WfsGetFeatureRequest } from "../../../types/Wfs/WfsGetFeatureRequest";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { TextboxButton } from "../../../@ui/ApiTable/Filtering/FilterDateRange";
import { DateTime } from "luxon";
import {
  endpointTypeOptions,
  geoSpatialOptions,
  InrixTripFormData,
  OptionType,
  providerTypeOptions,
  vehicleWeightClassTypeOptions,
} from "./InrixFormModel";
import ThemedSelect from "../../../@ui/ThemedSelect";
import { inrixSchema } from "./schema";
import { ErrorText } from "../../../@ui/ErrorText";

export interface InrixTripProps {
  boundaryId?: string;
}

export const InrixTrip = () => {
  const layerName = "Inrix:Trip";
  const { layers, getLayer, wfsOrder } = useDataSelectionContext();
  const layer = useMemo(() => getLayer(layerName), [layers]);

  const now = DateTime.local()
    .setZone("local")
    .set({ millisecond: 0, second: 0 });

  const initialValues = useMemo(
    () =>
      ({
        outputType: 1,
        startDate: now.minus({ days: 1, months: 3 }).toISODate() || "",
        endDate: now.minus({ days: 2 }).toISODate() || "",
        fromHour: 0,
        fromMinute: 0,
        toHour: 0,
        toMinute: 0,
        showTimes: false,
        geoSpatialMatchOn: geoSpatialOptions,
        includeTrajectories: false,
        providerTypes: providerTypeOptions,
        endpointTypes: endpointTypeOptions,
        vehicleWeightClasses: vehicleWeightClassTypeOptions,
      }) as InrixTripFormData,
    [layer],
  );

  const onSubmit = (values: InrixTripFormData) => {
    if (values.showTimes === false) {
      values.fromHour = 0;
      values.fromMinute = 0;
      values.toHour = 0;
      values.toMinute = 0;
    }
    const startDateUtc = DateTime.fromISO(values.startDate!, {
      zone: "UTC",
    })
      .startOf("day")
      .toISO();

    const endDateUtc = DateTime.fromISO(values.endDate!, {
      zone: "UTC",
    })
      .startOf("day")
      .toISO();
    const filter = `StartDateTime >= ${startDateUtc} AND EndDateTime <= ${endDateUtc} AND GeoSpatialMatchOn = [${values.geoSpatialMatchOn?.map((option) => option.value).join(", ")}] AND ProviderTypes = [${values.providerTypes?.map((option) => option.value).join(", ")}] AND EndpointTypes = [${values.endpointTypes?.map((option) => option.value).join(", ")}] AND VehicleWeightClasses = [${values.vehicleWeightClasses?.map((option) => option.value).join(", ")}]`;

    wfsOrder({
      service: "WFS",
      version: "2.0.0",
      request: "GetFeature",
      resultType: "Results",
      typeNames: layerName,
      outputFormat:
        values.outputType === 1 ? "csv"
          : values.outputType === 2 ? "shapefile"
            : values.outputType === 3 ? "parquet"
              : "shapefile",
      filter: filter,
    } as WfsGetFeatureRequest);
  };

  return (
    <>
      <DataSelectionModel
        headerText="Inrix Trip"
        initialValues={initialValues}
        layer={layer}
        onSubmit={onSubmit}
        validationSchema={inrixSchema}
        size="xl"
      >
        {({ values, setFieldValue, errors }) => (
          <>
            <Row className="justify">
              <Col className="col-md-6">
                <Form.Group>
                  <Form.Label className="mb-0 mt-2">Start date</Form.Label>
                  <InputGroup className="w-100">
                    <Form.Control
                      className=" mr-1"
                      type="date"
                      min={DateTime.fromISO("2006-11-06").toISODate() || ""}
                      max={DateTime.local().minus({ days: 2 }).toISODate() || ""}
                      value={values.startDate}
                      onChange={(e) =>
                        setFieldValue("startDate", e.target.value)
                      }
                      name="startDate"
                      style={{ borderRight: "none", paddingRight: 0 }}
                    />
                    <TextboxButton
                      onClick={() => setFieldValue("startDate", "")}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </TextboxButton>
                  </InputGroup>
                </Form.Group>
                {errors.startDate && (
                  <ErrorText className="text-danger">
                    {errors.startDate}
                  </ErrorText>
                )}
              </Col>
              {values.showTimes && (
                <Col className="col-md-6">
                  <Form.Group>
                    <Form.Label className="mb-0 mt-2">Start time</Form.Label>
                    <TimeSelectGroup
                      hourName="fromHour"
                      minuteName="fromMinute"
                      setFieldValue={setFieldValue}
                      hourValue={values.fromHour}
                      minuteValue={values.fromMinute}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>
            <Row className="justify">
              <Col className="col-md-6">
                <Form.Group>
                  <Form.Label className="mb-0 mt-2">End date</Form.Label>
                  <InputGroup className="w-100">
                    <Form.Control
                      className=" mr-1"
                      type="date"
                      value={values.endDate}
                      min={DateTime.fromISO("2006-11-06").toISODate() || ""}
                      max={DateTime.local().minus({ days: 2 }).toISODate() || ""}
                      onChange={(e) => setFieldValue("endDate", e.target.value)}
                      name="endDate"
                      style={{ borderRight: "none", paddingRight: 0 }}
                    />
                    <TextboxButton onClick={() => setFieldValue("endDate", "")}>
                      <FontAwesomeIcon icon={faXmark} />
                    </TextboxButton>
                  </InputGroup>
                </Form.Group>
                {errors.endDate && (
                  <ErrorText className="text-danger">
                    {errors.endDate}
                  </ErrorText>
                )}
              </Col>
              {values.showTimes && (
                <Col className="col-md-6">
                  <Form.Group>
                    <Form.Label className="mb-0 mt-2">End time</Form.Label>
                    <TimeSelectGroup
                      hourName="toHour"
                      minuteName="toMinute"
                      setFieldValue={setFieldValue}
                      hourValue={values.toHour}
                      minuteValue={values.toMinute}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>
            <Row className="justify">
              <Col className="col-md-12">
                <Form.Group>
                  <Form.Label className="mb-0 mt-2">
                    Geospatial Match
                  </Form.Label>
                  <ThemedSelect<OptionType, true>
                    isMulti
                    options={geoSpatialOptions}
                    value={values.geoSpatialMatchOn}
                    onChange={(selected) =>
                      setFieldValue("geoSpatialMatchOn", selected || [])
                    }
                  />
                </Form.Group>
                {errors.geoSpatialMatchOn && (
                  <ErrorText className="text-danger">
                    {errors.geoSpatialMatchOn}
                  </ErrorText>
                )}
              </Col>
            </Row>
            <Row className="justify">
              <Col className="col-md-12">
                <Form.Group>
                  <Form.Label className="mb-0 mt-2">Endpoint Types</Form.Label>
                  <ThemedSelect<OptionType, true>
                    isMulti
                    options={endpointTypeOptions}
                    value={values.endpointTypes}
                    onChange={(selected) =>
                      setFieldValue("endpointTypes", selected || [])
                    }
                  />
                </Form.Group>
                {errors.endpointTypes && (
                  <ErrorText className="text-danger">
                    {errors.endpointTypes}
                  </ErrorText>
                )}
              </Col>
            </Row>
            <Row className="justify">
              <Col className="col-md-12">
                <Form.Group>
                  <Form.Label className="mb-0 mt-2">Provider Types</Form.Label>
                  <ThemedSelect<OptionType, true>
                    isMulti
                    options={providerTypeOptions}
                    value={values.providerTypes}
                    onChange={(selected) =>
                      setFieldValue("providerTypes", selected || [])
                    }
                  />
                </Form.Group>
                {errors.providerTypes && (
                  <ErrorText className="text-danger">
                    {errors.providerTypes}
                  </ErrorText>
                )}
              </Col>
            </Row>
            <Row className="justify">
              <Col className="col-md-12">
                <Form.Group>
                  <Form.Label className="mb-0 mt-2">
                    Vehicle weight classes
                  </Form.Label>
                  <ThemedSelect<OptionType, true>
                    isMulti
                    options={vehicleWeightClassTypeOptions}
                    value={values.vehicleWeightClasses}
                    onChange={(selected) =>
                      setFieldValue("vehicleWeightClasses", selected || [])
                    }
                  />
                </Form.Group>
                {errors.vehicleWeightClasses && (
                  <ErrorText className="text-danger">
                    {errors.vehicleWeightClasses}
                  </ErrorText>
                )}
              </Col>
            </Row>
            <Row className="justify">
              <Col className="col-md-12">
                <Form.Group>
                  <Form.Label className="mb-0 mt-2">Output as</Form.Label>
                  <div>
                    <Form.Control
                      as="select"
                      name="outputType"
                      className="form-select"
                      value={values.outputType}
                      onChange={(e) =>
                        setFieldValue("outputType", parseInt(e.target.value))
                      }
                    >
                      <option value={1}>CSV</option>
                    </Form.Control>
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </>
        )}
      </DataSelectionModel>
    </>
  );
};
